import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, UserRoundPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import {
  guardianshipsService,
  type GuardianRequest,
} from '../../services/guardianships';
import GuardianDeclarationDialog from './GuardianDeclarationDialog';

interface GuardianRequestsProps {
  /** Told when a request is answered, so a page can refresh what depends on it. */
  onAnswered?: () => void;
}

/**
 * The children waiting on this account: somebody entered a person under 14 in
 * a competition and named this address as their parent's or guardian's.
 *
 * It lives here rather than only behind the letter's link because an email is
 * a bad place to keep a pending decision: it gets buried, deleted, or read on
 * a phone that is not the one signed in. So the same request stands on the
 * dashboard and in the profile **until it is answered** — opened or refused —
 * and answering it needs no token: the address on the entry is the address
 * this account signs in with, which is where the letter went.
 *
 * Refusing is a real answer, not a dismissal: it is recorded on the entry, so
 * the organiser who typed the address learns they got it wrong, and the
 * request stops coming back.
 *
 * Renders nothing at all when there is nothing waiting.
 */
export default function GuardianRequests({ onAnswered }: GuardianRequestsProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const [requests, setRequests] = useState<GuardianRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<GuardianRequest | null>(null);
  const [saving, setSaving] = useState(false);
  const [decliningId, setDecliningId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRequests(await guardianshipsService.requests());
    } catch {
      // A homepage must not turn red because one panel could not load.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const answered = (request: GuardianRequest) => {
    setRequests((current) => current.filter((r) => r.key !== request.key));
    onAnswered?.();
  };

  const confirmOpen = async () => {
    if (!opening || saving) return;
    setSaving(true);
    try {
      const result = await guardianshipsService.open({
        participantId: opening.participant_id,
        wardId: opening.existing_ward_id,
        locale: i18n.language,
      });
      toast({ title: t('guardian.accept.opened', { name: result.ward.full_name }) });
      answered(opening);
      setOpening(null);
    } catch (error) {
      toast({
        title: t('guardian.accept.openError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const decline = async (request: GuardianRequest) => {
    if (!window.confirm(t('guardian.requests.confirmDecline', { name: request.full_name }))) return;

    setDecliningId(request.key);
    try {
      await guardianshipsService.decline({ participantId: request.participant_id });
      toast({ title: t('guardian.requests.declined', { name: request.full_name }) });
      answered(request);
    } catch (error) {
      toast({
        title: t('guardian.requests.declineError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setDecliningId(null);
    }
  };

  if (loading || requests.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="guardian-requests">
      {requests.map((request) => (
        <div
          key={request.key}
          className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4"
          data-testid="guardian-request"
        >
          <div className="flex items-start gap-3">
            <UserRoundPlus className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
            <div className="min-w-0">
              <p className="font-medium">
                {t('guardian.requests.title', { name: request.full_name, event: request.event_name })}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('guardian.requests.description', { name: request.name ?? request.full_name })}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {request.entries.map((entry) => entry.competition_name).join(' · ')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setOpening(request)}>
              {request.existing_ward_id
                ? t('guardian.accept.thisIs', { name: request.name ?? request.full_name })
                : t('guardian.accept.openFor', { name: request.name ?? request.full_name })}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={decliningId === request.key}
              onClick={() => decline(request)}
            >
              {decliningId === request.key && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('guardian.requests.decline')}
            </Button>
          </div>
        </div>
      ))}

      {opening && (
        <GuardianDeclarationDialog
          name={opening.name ?? opening.full_name}
          existing={!!opening.existing_ward_id}
          saving={saving}
          onOpenChange={(open) => {
            if (!open) setOpening(null);
          }}
          onConfirm={confirmOpen}
        />
      )}
    </div>
  );
}
